import clsx from "clsx";
import { FC, memo, ReactNode } from "react";

import type { ChatMessageTool } from "@tsmono/inspect-common/types";
import type { MarkdownReference } from "@tsmono/react/components";
import { getOwn } from "@tsmono/util";

import {
  RowSelectCheckbox,
  useSelectableRow,
} from "../selection/RowSelectCheckbox";

import { ChatMessage } from "./ChatMessage";
import styles from "./ChatMessageRow.module.css";
import { MessageLabel } from "./MessageLabel";
import { hasServerToolUse, ResolvedMessage } from "./messages";
import { hasVisibleContent } from "./rowsModel";
import { ClientToolCall } from "./tools/ClientToolCall";
import { resolveToolInput, substituteToolCallContent } from "./tools/tool";
import {
  ChatViewDisplayOptions,
  ChatViewLabelOptions,
  ChatViewLinkingOptions,
  ChatViewToolOptions,
  ContentTool,
} from "./types";

interface ChatMessageRowProps {
  index: number;
  parentName: string;
  resolvedMessage: ResolvedMessage;
  references?: MarkdownReference[];
  className?: string | string[];
  display?: ChatViewDisplayOptions;
  labels?: ChatViewLabelOptions;
  linking?: ChatViewLinkingOptions;
  tools?: ChatViewToolOptions;
  maxLabelLength?: number;
  /** Global sequential number of this row's first rendered block. */
  startNumber?: number;
}

/**
 * Renders the ChatMessage component.
 */
export const ChatMessageRow = memo(function ChatMessageRow({
  index,
  parentName,
  resolvedMessage,
  references,
  className,
  display,
  labels,
  linking,
  tools,
  startNumber,
}: ChatMessageRowProps) {
  const highlightUserMessage = display?.highlightUserMessage ?? true;
  const showLabels = labels?.show ?? true;
  const labelValues = labels?.messageLabels;
  const highlightLabeled = labels?.highlight ?? false;
  const toolCallStyle = tools?.callStyle ?? "complete";
  const getCustomToolView = tools?.renderToolCall;

  // Evidence selection, when the list has selection mode on: the checkbox
  // renders in the first block's header, the row highlights when selected.
  // Selection rides context (not props) so a toggle re-renders only the
  // rows whose state changed; chatMessageRowEqual needs no update.
  const rowSelection = useSelectableRow(
    resolvedMessage.message.id ?? undefined
  );
  const headerLeading = rowSelection ? (
    <RowSelectCheckbox selection={rowSelection} item="message" />
  ) : undefined;

  const views: ReactNode[] = [];
  const viewKinds: Array<"message" | "tool"> = [];
  const viewChips: Array<ReactNode | undefined> = [];
  const useLabels = showLabels || Object.keys(labelValues || {}).length > 0;

  // A block's label is its scanner citation (keyed by message id) when a label
  // map is supplied, otherwise its global sequential number. Tools carry their
  // own number but inherit the parent message's citation.
  const baseNumber = startNumber ?? index + 1;
  const labelForBlock = (
    blockId: string | null | undefined,
    blockNumber: number
  ): string | undefined =>
    labelValues && blockId ? getOwn(labelValues, blockId) : String(blockNumber);

  const hasToolCalls =
    toolCallStyle !== "omit" &&
    resolvedMessage.message.role === "assistant" &&
    !!resolvedMessage.message.tool_calls &&
    resolvedMessage.message.tool_calls.length > 0;

  // Skip the assistant chat message entirely when it has no visible text
  // or reasoning content and the tool calls will be rendered as their own
  // views below — otherwise it leaves an empty, padded block. The row label
  // (if any) gets hoisted onto the first tool call below.
  const skipChatMessage =
    hasToolCalls && !hasVisibleContent(resolvedMessage.message);

  // The message occupies the first block number of the row (when rendered);
  // its tool calls take the numbers that follow.
  const rowLabel = useLabels
    ? labelForBlock(resolvedMessage.message.id, baseNumber)
    : undefined;

  // The position label renders as a chip on the right of the message's role
  // line (inside the card), not as a separate column.
  const messageChip =
    !skipChatMessage && rowLabel?.trim() ? (
      <MessageLabel label={rowLabel} />
    ) : undefined;

  if (!skipChatMessage) {
    views.push(
      <ChatMessage
        id={`${parentName}-chat-messages-${index}`}
        message={resolvedMessage.message}
        display={display}
        linking={linking}
        references={references}
        label={messageChip}
        headerLeading={headerLeading}
      />
    );
    viewKinds.push("message");
    viewChips.push(undefined);
  }

  // The first tool's number follows the message block (if rendered).
  let toolNumber = baseNumber + (skipChatMessage ? 0 : 1);

  // The tool messages associated with this chat message
  if (
    toolCallStyle !== "omit" &&
    resolvedMessage.message.role === "assistant" &&
    resolvedMessage.message.tool_calls &&
    resolvedMessage.message.tool_calls.length
  ) {
    const toolMessages = resolvedMessage.toolMessages;
    let idx = 0;
    for (const tool_call of resolvedMessage.message.tool_calls) {
      // Extract tool input
      const { name, input, description, functionCall, contentType, title } =
        resolveToolInput(tool_call.function, tool_call.arguments);

      let toolMessage: ChatMessageTool | undefined;
      if (tool_call.id) {
        toolMessage = toolMessages.find(
          (msg) => msg.tool_call_id === tool_call.id
        );
      } else {
        toolMessage = toolMessages[idx];
      }

      // Resolve the tool output
      const resolvedToolOutput = resolveToolMessage(toolMessage);

      const resolvedToolView = tool_call.view
        ? substituteToolCallContent(tool_call.view, tool_call.arguments)
        : undefined;

      // Each tool call is one self-contained block: collapsible header with
      // the input zone and output well beneath. Compact mode keeps the
      // single line.
      if (toolCallStyle === "compact") {
        views.push(
          <ToolCallViewCompact idx={idx} functionCall={functionCall} />
        );
        viewKinds.push("tool");
        viewChips.push(undefined);
      } else {
        views.push(
          <ClientToolCall
            id={`${index}-tool-call-${idx}`}
            key={`tool-call-${idx}`}
            tool={name}
            title={resolvedToolView?.title || title}
            functionCall={functionCall}
            input={input}
            description={description}
            contentType={contentType}
            output={resolvedToolOutput}
            error={toolMessage?.error ?? undefined}
            view={resolvedToolView}
            getCustomToolView={getCustomToolView}
            // The head chat message is skipped for tool-only turns, so the
            // row's checkbox lives on the first tool call's header.
            headerLeading={
              skipChatMessage && idx === 0 ? headerLeading : undefined
            }
          />
        );
        viewKinds.push("tool");
        // The position chip sits on the tool block. Tools inherit the parent
        // message's citation when a label map is supplied.
        const toolLabel = useLabels
          ? labelForBlock(resolvedMessage.message.id, toolNumber)
          : undefined;
        viewChips.push(
          toolLabel?.trim() ? <MessageLabel label={toolLabel} /> : undefined
        );
      }
      toolNumber++;

      idx++;
    }
  }

  const hasTools = viewKinds.some((k) => k !== "message");

  // Use the peer-block layout (which emits the `data-message-kind` bands the
  // theme colors, and the connected blue tool-call/output card) whenever the
  // row has tools — even with labels off, e.g. model-event views — so tool
  // rendering matches the messages tab. Label chips still only show when
  // `useLabels`; here they're all undefined, so none render.
  if (useLabels || hasTools) {
    // Each row part is a peer block: the message in its role band, the tool
    // call in the blue tool box, and the tool output in a plain bordered box.
    const renderPart = (idx: number) => {
      const kind = viewKinds[idx];
      const isMessage = kind === "message";
      // A message with server-side tool calls is a seamless turn container:
      // ChatMessage renders the band/tool rows internally, so the wrapper
      // carries the container frame instead of the role band.
      const isTurn = isMessage && hasServerToolUse(resolvedMessage.message);
      const chip = viewChips[idx];
      return (
        <div
          key={`chat-message-row-${index}-part-${idx}`}
          data-message-role={
            isMessage && !isTurn ? resolvedMessage.message.role : undefined
          }
          data-message-kind={isMessage ? kind : undefined}
          className={clsx(
            isMessage && !isTurn && styles.container,
            isTurn
              ? styles.turnContainer
              : isMessage && hasTools
                ? styles.box
                : undefined,
            !isMessage ? styles.toolPart : undefined,
            isMessage &&
              !isTurn &&
              highlightUserMessage &&
              resolvedMessage.message.role === "user"
              ? styles.user
              : undefined,
            isMessage && !isTurn && !hasTools && idx === 0
              ? styles.first
              : undefined,
            isMessage && !isTurn && !hasTools && idx === views.length - 1
              ? styles.last
              : undefined,
            highlightLabeled && isMessage && messageChip
              ? styles.highlight
              : undefined
          )}
        >
          {chip ? <div className={styles.toolLabel}>{chip}</div> : null}
          {views[idx]}
        </div>
      );
    };

    return (
      <div
        className={clsx(
          styles.grid,
          className,
          rowSelection?.selected && styles.selected
        )}
      >
        {views.map((_, idx) => renderPart(idx))}
      </div>
    );
  } else {
    const isTurn = hasServerToolUse(resolvedMessage.message);
    const parts = views.map((view, idx) => {
      return (
        <div
          key={`chat-message-row-unlabeled-${index}-part-${idx}`}
          data-message-role={isTurn ? undefined : resolvedMessage.message.role}
          className={clsx(
            isTurn ? styles.turnContainer : styles.container,
            !isTurn && idx === 0 ? styles.first : undefined,
            !isTurn && idx === views.length - 1 ? styles.last : undefined,
            idx === views.length - 1 ? styles.bottomMargin : undefined,
            className,
            !isTurn && styles.simple,
            !isTurn &&
              highlightUserMessage &&
              resolvedMessage.message.role === "user"
              ? styles.user
              : undefined
          )}
        >
          {view}
        </div>
      );
    });
    // No single row container here, so in selection mode a frame wraps the
    // parts; it stays mounted across toggles so the parts keep their state.
    return rowSelection ? (
      <div className={clsx(rowSelection.selected && styles.selected)}>
        {parts}
      </div>
    ) : (
      parts
    );
  }
}, chatMessageRowEqual);

// Shallow-compare every prop by reference EXCEPT `resolvedMessage`: compare
// that one through the wrapper to its `message`/`toolMessages` references,
// because `resolveMessages` recreates the wrapper each call even when the
// underlying turn is unchanged (so default memo would never hit). Iterating
// the keys keeps this exhaustive by construction — a future prop is covered by
// the generic `===` branch without editing this function. Callers must pass
// stable option objects (and never mutate a message in place) for it to hit;
// the actively-streaming turn gets a fresh message ref each poll, so it still
// re-renders.
function chatMessageRowEqual(
  prev: ChatMessageRowProps,
  next: ChatMessageRowProps
): boolean {
  const keys: (keyof ChatMessageRowProps)[] = Object.keys(prev).filter(
    (key): key is keyof ChatMessageRowProps => key in prev
  );
  if (keys.length !== Object.keys(next).length) return false;
  for (const key of keys) {
    if (key === "resolvedMessage") {
      const a = prev.resolvedMessage;
      const b = next.resolvedMessage;
      if (a.message !== b.message) return false;
      if (a.toolMessages.length !== b.toolMessages.length) return false;
      if (!a.toolMessages.every((t, i) => t === b.toolMessages[i])) {
        return false;
      }
    } else if (prev[key] !== next[key]) {
      return false;
    }
  }
  return true;
}

const resolveToolMessage = (toolMessage?: ChatMessageTool): ContentTool[] => {
  if (!toolMessage || toolMessage.error) {
    return [];
  }

  const content = toolMessage.content;
  if (typeof content === "string") {
    return [
      {
        type: "tool",
        content: [
          {
            type: "text",
            text: content,
            refusal: null,
            internal: null,
            citations: null,
          },
        ],
      },
    ];
  } else {
    const result = content
      .map((con): ContentTool | undefined => {
        if (typeof con === "string") {
          return {
            type: "tool",
            content: [
              {
                type: "text",
                text: con,
                refusal: null,
                internal: null,
                citations: null,
              },
            ],
          } satisfies ContentTool;
        } else if (con.type !== "tool_use") {
          return {
            content: [con],
            type: "tool",
          } satisfies ContentTool;
        }
      })
      .filter((con) => con !== undefined);
    return result;
  }
};

const ToolCallViewCompact: FC<{
  idx: number;
  functionCall: string;
}> = ({ idx, functionCall }) => {
  return (
    <div key={`tool-call-${idx}`}>
      <code className={clsx(styles.codeCompact)}>tool: {functionCall}</code>
    </div>
  );
};
